/*
 * Dark Web Monitoring page.
 *
 * Headline stats (total breach mentions, emails breached, credential
 * leaks, email-only mentions), cumulative-exposure dual-bar chart and per-email
 * breach table.
 *
 * Calculations:
 *   - summary maps /getorgstats: totalBreaches=breachedUsers.allTotal,
 *     emailsBreached=breachedUsers.allUnique, credentialBreaches=passwords.withPass,
 *     emailOnlyBreaches=passwords.withoutPassOrHash, emailsScanned=assets.total.
 *   - byEmail maps /breachedusers rows (sorted by mentions DESC).
 *
 * Live data:
 *   - Summary stats + per-email breach rows are pulled from dwm-api
 *     (`useGetDwmOrgStatsQuery` / `useGetDwmBreachedUsersQuery`), both
 *     tenancy-scoped. Cached 6 hours (breach feeds refresh infrequently).
 *   - The dwm-api has no name/department on breach rows, so those columns
 *     are joined from tenancy user data (`useGetUsersQuery`) by email.
 *   - The cumulative exposure chart is backed by sp_GetBreachTrend via
 *     `useGetDwmBreachTrendQuery`, scoped to the shell's selected date range.
 *     The proc returns all-time daily data (cached per org in BreachService);
 *     the API filters to the range, rolls it up to an auto-chosen granularity,
 *     zero-fills gaps and echoes the granularity back. The browser only maps
 *     display labels; TrendChart still computes the cumulative running total.
 *   - The API carries no distinct-source count or latest-breach date per
 *     user, so those two columns show '-' until an endpoint provides them.
 *
 * Gated by FEATURES.darkWebMonitoring.
 */
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Grid, Button, Tooltip, CircularProgress } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import { motion } from 'framer-motion';
import MainCard from '../../../components/cards/MainCard';
import { useGetDwmOrgStatsQuery, useGetDwmBreachStatsQuery, useGetDwmBreachedUsersQuery, useGetDwmBreachTrendQuery } from '../../../store/services/dwm-service';
import { useGetUsersQuery } from '../../../store/services/user-service';

// The API returns a complete, zero-filled, chronologically ordered series for the
// selected range and echoes the granularity it chose (collation + padding happen
// server-side in BreachService), so the browser only formats a display label per
// bucket. Bucket keys: "YYYY-MM" (month), "YYYY-Www" (ISO week), "YYYY-MM-DD" (day).
function formatBucketLabel(bucket, granularity) {
    if (granularity === 'week') return bucket.slice(5); // "W07"
    if (granularity === 'day') {
        const d = new Date(`${bucket}T00:00:00Z`);
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }
    const [y, m] = bucket.split('-').map(Number); // month
    return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
}

// The dwm-api breach rows carry no display name, so fall back to a readable
// name derived from the email local-part when the tenancy join misses.
function deriveNameFromEmail(email) {
    if (!email) return '';
    const local = String(email).split('@')[0];
    return local
        .split(/[._-]+/)
        .filter(Boolean)
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ') || email;
}

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

// Left-aligned "View" affordance for drillable rows: replaces whole-row
// click so the click target is explicit, matching the rest of the portal.
function RowViewButton({ onClick }) {
    return (
        <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            sx={{
                borderRadius: '4px', borderColor: 'divider', color: 'text.secondary',
                fontSize: 11, fontWeight: 600, py: '2px', px: '10px', minWidth: 0, textTransform: 'none',
                '&:hover': { borderColor: 'secondary.main', color: 'secondary.main', bgcolor: 'transparent' },
            }}
        >
            View
        </Button>
    );
}

function StatCard({ label, value, sub, color, icon: Icon, loading }) {
    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper', height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {Icon && (
                    <Box aria-hidden="true" sx={{ position: 'absolute', top: 18, right: 18, display: 'flex', color: color || 'text.disabled', opacity: color ? 0.85 : 0.5 }}>
                        <Icon sx={{ fontSize: 22 }} />
                    </Box>
                )}
                <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.disabled', pr: 4 }}>{label}</Typography>
                {/* Spinner while (re)fetching so a date change reads as "updating" rather
                    than silently showing the previous range's number. */}
                {loading ? (
                    <Box sx={{ height: 28, mt: 0.5, display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={18} color="secondary" />
                    </Box>
                ) : (
                    <Typography sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1, mt: 0.5, color: color || 'text.primary' }}>
                        {typeof value === 'number' ? value.toLocaleString() : value}
                    </Typography>
                )}
                {sub && <Typography sx={{ fontSize: 10, color: 'text.secondary', mt: 'auto', pt: 0.5 }}>{sub}</Typography>}
            </Box>
        </MainCard>
    );
}

// Round an axis maximum up to a clean tick value so the gridline labels read
// as whole, human numbers (10, 25, 250) rather than the raw running total.
function niceMax(v) {
    if (v <= 5) return 5;
    const mag = Math.pow(10, Math.floor(Math.log10(v)));
    const n = v / mag;
    const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
    return step * mag;
}

// Cumulative breach-mention area chart. A single filled area of the running total of
// breach mentions within the selected period. The total starts at zero at the start of
// the range (it does NOT carry a pre-window baseline) so a few mentions over a short
// window are clearly visible instead of being a flat blip on top of a large all-time
// total. Per-point hover strips report the bucket detail.
function TrendChart({ data }) {
    const PAD_L = 34;   // gutter for the y-axis tick labels
    const LABEL_H = 18; // reserved band for month labels

    // Running total within the selected range, starting from zero.
    const cum = useMemo(() => {
        let total = 0;
        return data.map((d) => {
            total += d.value;
            return { label: d.label, key: d.key, total, delta: d.value };
        });
    }, [data]);

    const max = niceMax(cum.length ? cum[cum.length - 1].total : 1);

    // Fill the width the card gives us; keep a fixed drawing height so the
    // curve aspect ratio stays consistent regardless of container width.
    const chartRef = useRef(null);
    const [w, setW] = useState(760);
    // Index of the bucket currently under the pointer, for the hover dot.
    const [hover, setHover] = useState(null);
    useEffect(() => {
        const el = chartRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            const r = entries[0]?.contentRect;
            if (r && r.width > 0) setW(r.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const W = Math.max(280, w);
    const H = 150;
    const plotW = W - PAD_L;

    // Area geometry: points span the plot width, sized against the shared y scale.
    const n = cum.length || 1;
    const x = (i) => (n === 1 ? PAD_L + plotW / 2 : PAD_L + (plotW * i) / (n - 1));
    const y = (v) => H - (v / max) * H;

    // Cumulative breach-mention area (red), filled down to the x-axis (y=0). Built as a
    // filled <path>: "M first L rest Z", down the bottom edge and back along the running total.
    const pathFrom = (pts) => `M ${pts[0]} L ${pts.slice(1).join(' L ')} Z`;
    const area = pathFrom([
        `${x(0)},${H}`,
        ...cum.map((d, i) => `${x(i)},${y(d.total)}`),
        `${x(n - 1)},${H}`
    ]);
    const totalLine = cum.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i)},${y(d.total)}`).join(' ');

    const ticks = [0, 0.25, 0.5, 0.75, 1];
    const stripW = plotW / n;

    return (
        <Box ref={chartRef} sx={{ width: '100%' }}>
            <Box component="svg" viewBox={`0 0 ${W} ${H + LABEL_H}`} preserveAspectRatio="none" sx={{ width: '100%', display: 'block' }}>
                {/* Gridlines + y-axis ticks */}
                {ticks.map((t) => (
                    <g key={t}>
                        <line x1={PAD_L} x2={W} y1={H - t * H} y2={H - t * H} stroke="var(--chart-grid)" strokeWidth="1" />
                        <text x={PAD_L - 6} y={H - t * H + 3} textAnchor="end" fontSize="9" fontFamily="Inter,sans-serif" fill="var(--chart-tick)">
                            {Math.round(t * max)}
                        </text>
                    </g>
                ))}

                {/* Area (only meaningful with >=2 points). The stroke uses
                    non-scaling-stroke so it stays crisp under the non-uniform stretch. */}
                {n > 1 && (
                    <>
                        <path d={area} fill="var(--red)" opacity={0.85} />
                        <path d={totalLine} fill="none" stroke="var(--red)" strokeWidth="1.5" opacity={0.9} vectorEffect="non-scaling-stroke" />
                    </>
                )}

                {/* Per-point hover strips (full-height, transparent) carry the month
                    detail, plus month labels every 3rd bucket. */}
                {cum.map((d, i) => {
                    const cx = x(i);
                    const title = `${d.label} - ${d.total} breach mentions (cumulative) - +${d.delta} this period`;
                    return (
                        <g key={d.key}>
                            <Tooltip title={title} placement="top" arrow>
                                <rect
                                    x={cx - stripW / 2} y={0} width={stripW} height={H}
                                    fill="transparent"
                                    onMouseEnter={() => setHover(i)}
                                    onMouseLeave={() => setHover((h) => (h === i ? null : h))}
                                    style={{ cursor: 'default' }}
                                />
                            </Tooltip>
                            {i % 3 === 0 && (
                                <text x={cx} y={H + 14} textAnchor="middle" fontSize="9" fontFamily="Inter,sans-serif" fill="var(--chart-tick)">
                                    {d.label}
                                </text>
                            )}
                        </g>
                    );
                })}

                {/* Highlighted dot on the running-total line for the hovered bucket.
                    pointer-events none so it never steals hover from the strips. */}
                {hover != null && cum[hover] && (
                    <circle
                        cx={x(hover)} cy={y(cum[hover].total)} r={4}
                        fill="var(--red)" stroke="var(--surface)" strokeWidth={2}
                        style={{ pointerEvents: 'none' }}
                    />
                )}
            </Box>
        </Box>
    );
}

export default function DwmPage({ drill, startDate, endDate }) {

    // Resolve the active tenancy the same way the rest of the portal does:
    // explicit URL override, then the Redux tenancy slice, then the logged-in
    // user. Every dwm-api endpoint is tenancy-scoped so this must be present.
    const reduxTenancyId = useSelector((state) => state.tenancy.tenancyId);
    const tenancyId = useMemo(() => {
        const urlId = new URLSearchParams(window.location.search).get('tenancyId');
        if (urlId && urlId !== 'undefined') return parseInt(urlId, 10);
        if (reduxTenancyId) return parseInt(reduxTenancyId, 10);
        try { return JSON.parse(localStorage.getItem('user'))?.tenancyId; } catch { return undefined; }
    }, [reduxTenancyId]);

    const { data: orgStats, isLoading: statsLoading, isError: statsError } =
        useGetDwmOrgStatsQuery({ tenancyId }, { skip: !tenancyId });
    // Headline tiles - one call feeds all four. allTimeRows is the static all-time
    // figure (server-cached, same for every range); the rest are windowed to the
    // selected dates. isLoading gates tile 1 (first paint only, so the unchanging
    // all-time number never flickers on a date change); isFetching gates the period
    // tiles so they visibly refresh when the range changes.
    const { data: periodStats, isLoading: statsFirstLoad, isFetching: periodFetching } =
        useGetDwmBreachStatsQuery({ tenancyId, startDate, endDate }, { skip: !tenancyId });
    // isFetching (not isLoading) so the table spinner also shows on refetches when
    // the date range changes - otherwise the stale list lingers and looks unresponsive.
    const { data: breached, isFetching: breachedLoading, isError: breachedError } =
        useGetDwmBreachedUsersQuery({ tenancyId, page: 1, limit: 100, startDate, endDate }, { skip: !tenancyId });
    // Bucket size follows the selected span so short ranges don't collapse to a single
    // bar: day for up to ~6 weeks (e.g. 7d/30d), week up to ~5 months (e.g. 90d), month
    // beyond (6m/12m). This keeps the default 6-month view on months rather than weeks.
    const trendGranularity = useMemo(() => {
        if (!startDate || !endDate) return 'month';
        const days = Math.round((new Date(endDate) - new Date(startDate)) / 86400000) + 1;
        if (days <= 45) return 'day';
        if (days <= 150) return 'week';
        return 'month';
    }, [startDate, endDate]);
    const { data: trendData, isLoading: trendLoading } =
        useGetDwmBreachTrendQuery({ tenancyId, startDate, endDate, granularity: trendGranularity }, { skip: !tenancyId });
    // Pulled once so breach rows can be joined to a name/department by email.
    const { data: tenancyUsers } = useGetUsersQuery(
        { tenancyId, perPage: 100000, page: 0, includes: '', query: '', extendedResults: true },
        { skip: !tenancyId }
    );

    const userMap = useMemo(() => {
        const m = new Map();
        for (const u of tenancyUsers?.data || []) {
            if (u?.email) m.set(String(u.email).toLowerCase(), u);
        }
        return m;
    }, [tenancyUsers]);

    // Headline numbers from /getorgstats. Credential vs email-only split comes
    // from the password analysis; emails scanned from the monitored asset count.
    const summary = useMemo(() => {
        const bu = orgStats?.breachedUsers || {};
        const pw = orgStats?.passwords || {};
        const assets = orgStats?.assets || {};
        return {
            totalBreaches: bu.allTotal ?? 0,
            emailsBreached: bu.allUnique ?? 0,
            credentialBreaches: pw.withPass ?? 0,
            emailOnlyBreaches: pw.withoutPassOrHash ?? 0,
            emailsScanned: assets.total ?? pw.total ?? 0,
        };
    }, [orgStats]);

    // Per-email rows from /breachedusers, joined to tenancy name/department.
    // mentions = total breach records; withCreds = records exposing a password.
    const byEmail = useMemo(() => {
        return (breached?.users || []).map((u) => {
            const match = userMap.get(String(u.email || '').toLowerCase());
            const joinedName = match ? `${match.firstName || ''} ${match.lastName || ''}`.trim() : '';
            return {
                email: u.email,
                name: joinedName || deriveNameFromEmail(u.email),
                dept: match?.department || '-',
                mentions: u.breaches ?? 0,
                withCreds: u.passwordAvailable ?? 0,
                // dwm-api has no distinct-source count or latest-breach date yet.
                sources: '-',
                latest: '-',
            };
        }).sort((a, b) => b.mentions - a.mentions);
    }, [breached, userMap]);


    // API already returns a complete ordered breach-mention series; just attach display
    // labels using the granularity the API chose.
    const trend = useMemo(() => {
        const gran = trendData?.granularity || 'month';
        return (trendData?.points || []).map((r) => ({
            key: r.bucket,
            label: formatBucketLabel(r.bucket, gran),
            value: Number(r.mentions) || 0
        }));
    }, [trendData]);

    // The breached-users table is the slow query (~10s), so it is NOT part of the
    // page-level gate - the summary and chart render immediately and the table shows
    // its own spinner and fills in when ready.
    const loading = statsLoading || trendLoading;
    const errored = statsError && breachedError;

    // The table is a top-N view (fetch capped at 100, render capped at 50), so its own
    // row count can't represent the total. Report the authoritative windowed totals from
    // the tiles instead - emails from tile 4, breach records from tile 2 - so the header
    // agrees with them; the rows below are just the top slice by mentions.
    const listShown = Math.min(byEmail.length, 50);
    const listEmails = periodStats?.distinctEmails ?? byEmail.length;
    const listMentions = periodStats?.totalMentions ?? byEmail.reduce((s, e) => s + e.mentions, 0);

    if (!tenancyId) {
        return (
            <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 0.5 }}>No tenancy selected</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Select a tenancy to view dark web monitoring data.</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ p: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={28} color="secondary" />
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Loading dark web monitoring data...</Typography>
            </Box>
        );
    }

    if (errored) {
        return (
            <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 0.5 }}>Could not load dark web monitoring data</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>The monitoring service is unavailable or this tenancy has no breach feed configured.</Typography>
            </Box>
        );
    }

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '6px 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <motion.div variants={fadeUp}>
                <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>Dark web monitoring</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                    </Typography>
                </Box>
            </motion.div>

            {/* Headline numbers */}
            <motion.div variants={fadeUp}>
                <Grid container spacing={1} alignItems="stretch" sx={{ '& > .MuiGrid-item': { display: 'flex' } }}>
                    <Grid item xs={6} md={3}><StatCard label="Total breach mentions" value={periodStats?.allTimeRows ?? 0} loading={statsFirstLoad} color="#FF5252" sub="all time - across all sources" icon={WarningAmberOutlinedIcon} /></Grid>
                    <Grid item xs={6} md={3}><StatCard label="Breach mentions in period" value={periodStats?.totalMentions ?? 0} loading={periodFetching} sub="within selected dates" icon={MailOutlineIcon} /></Grid>
                    <Grid item xs={6} md={3}><StatCard label="Plaintext password leaks" value={periodStats?.credMentions ?? 0} loading={periodFetching} color="#FF5252" sub="within selected dates" icon={LockOutlinedIcon} /></Grid>
                    <Grid item xs={6} md={3}><StatCard label="Distinct emails breached" value={periodStats?.distinctEmails ?? 0} loading={periodFetching} sub="within selected dates" icon={AlternateEmailOutlinedIcon} /></Grid>
                </Grid>
            </motion.div>

            {/* Trend */}
            <motion.div variants={fadeUp}>
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ px: 3, pt: 2.5, pb: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                                Cumulative exposure
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                Running total of breached records - over the selected period - hover for detail
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 10, color: 'text.secondary' }}>
                                <Box sx={{ width: 10, height: 10, bgcolor: 'var(--red)', borderRadius: '2px' }} />
                                Breach mentions
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ p: 3 }}>
                        <TrendChart data={trend} />
                    </Box>
                </MainCard>
            </motion.div>

            {/* Breakdown by email */}
            <motion.div variants={fadeUp}>
                <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
                    <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                                Most-mentioned emails
                            </Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                                {breachedLoading
                                    ? 'Loading breached emails...'
                                    : `${listEmails} emails appear in ${listMentions} breach records${listEmails > listShown ? ` - showing top ${listShown} by mentions` : ''} - click a row to drill in`}
                            </Typography>
                        </Box>
                    </Box>
                    {breachedLoading ? (
                        <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                            <CircularProgress size={24} color="secondary" />
                            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Fetching breached emails - this can take a few seconds</Typography>
                        </Box>
                    ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <Box component="thead">
                                <Box component="tr">
                                    <Box component="th" aria-hidden="true" sx={{ borderBottom: '1px solid', borderColor: 'divider', width: '1px' }} />
                                    {['Email', 'Name', 'Department', 'Mentions', 'Credentials', 'Distinct sources', 'Latest'].map((h) => (
                                        <Box key={h} component="th" sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', px: 2, py: 1.25, textAlign: 'left', borderBottom: '1px solid', borderColor: 'divider', whiteSpace: 'nowrap' }}>{h}</Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {byEmail.slice(0, 50).map((e) => (
                                    <Box component="tr" key={e.email}
                                        sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' }, '&:last-child': { borderBottom: 'none' } }}>
                                        <Box component="td" sx={{ px: 2, py: 1.25, width: '1px', whiteSpace: 'nowrap' }}>
                                            <RowViewButton onClick={() => drill('user-detail', e.name, { type: 'user', value: e.email })} />
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.primary', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap' }}>{e.email}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.secondary' }}>{e.name}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.secondary' }}>{e.dept}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, fontWeight: 700, color: 'text.primary' }}>{e.mentions}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25 }}>
                                            {e.withCreds > 0 ? (
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: '6px', py: '1px', borderRadius: '4px', fontSize: 10, fontWeight: 700, bgcolor: 'rgba(255,82,82,.1)', color: '#E53935', border: '1px solid rgba(255,82,82,.25)' }}>
                                                    <LockOutlinedIcon sx={{ fontSize: 11 }} />
                                                    {e.withCreds} with password
                                                </Box>
                                            ) : (
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.disabled', fontSize: 11 }}>
                                                    <MailOutlineIcon sx={{ fontSize: 11 }} />
                                                    email only
                                                </Box>
                                            )}
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.secondary' }}>{e.sources}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.25, color: 'text.disabled', fontSize: 11 }}>{e.latest}</Box>
                                    </Box>
                                ))}
                                {byEmail.length === 0 && (
                                    <Box component="tr"><Box component="td" colSpan={8} sx={{ px: 2, py: 4, textAlign: 'center', fontSize: 12, color: 'text.disabled' }}>No emails match this filter</Box></Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    )}
                </MainCard>
            </motion.div>
        </motion.div>
    );
}
